import React, { useState, useEffect } from 'react';
import { MapPin, Star, Plus, X, Search, Coffee, Loader, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CoffeeShopReviews = () => {
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [showAddShop, setShowAddShop] = useState(false);
  const [showAddReview, setShowAddReview] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mapCenter, setMapCenter] = useState({ lat: 49.7016, lng: -123.1558 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newShop, setNewShop] = useState({
    name: '',
    address: '',
    lat: '',
    lng: ''
  });

  const [newReview, setNewReview] = useState({
    author: '',
    rating: 5,
    comment: ''
  });

  // Load shops from API
  useEffect(() => {
    fetchShops();
  }, []);

  const fetchShops = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/shops`);
      if (!response.ok) throw new Error('Failed to fetch shops');
      const data = await response.json();
      setShops(data);
    } catch (err) {
      console.error('Error fetching shops:', err);
      setError('Failed to load coffee shops. Make sure the API server is running.');
    } finally {
      setLoading(false);
    }
  };

  const addShop = async () => {
    if (newShop.name && newShop.address && newShop.lat && newShop.lng) {
      try {
        const response = await fetch(`${API_URL}/shops`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: newShop.name,
            address: newShop.address,
            lat: parseFloat(newShop.lat),
            lng: parseFloat(newShop.lng)
          })
        });
        
        if (!response.ok) throw new Error('Failed to create shop');
        
        const createdShop = await response.json();
        setShops([...shops, createdShop]);
        setNewShop({ name: '', address: '', lat: '', lng: '' });
        setShowAddShop(false);
        setMapCenter({ lat: createdShop.lat, lng: createdShop.lng });
      } catch (err) {
        console.error('Error adding shop:', err);
        alert('Failed to add shop. Please try again.');
      }
    }
  };

  const addReview = async () => {
    if (selectedShop && newReview.author && newReview.comment) {
      try {
        const response = await fetch(`${API_URL}/shops/${selectedShop.id}/reviews`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            author: newReview.author,
            rating: newReview.rating,
            comment: newReview.comment
          })
        });
        
        if (!response.ok) throw new Error('Failed to create review');
        
        const createdReview = await response.json();
        
        // Refresh the shops to get updated ratings
        await fetchShops();
        
        // Update selected shop
        const updatedShop = shops.find(s => s.id === selectedShop.id);
        if (updatedShop) {
          setSelectedShop({
            ...updatedShop,
            reviews: [...updatedShop.reviews, createdReview]
          });
        }
        
        setNewReview({ author: '', rating: 5, comment: '' });
        setShowAddReview(false);
      } catch (err) {
        console.error('Error adding review:', err);
        alert('Failed to add review. Please try again.');
      }
    }
  };

  const filteredShops = shops.filter(shop =>
    shop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    shop.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <Star
            key={star}
            size={16}
            className={star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}
          />
        ))}
        <span className="ml-1 text-sm text-gray-600">{rating.toFixed(1)}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="animate-spin text-amber-600 mx-auto mb-4" size={48} />
          <p className="text-amber-900 text-lg">Loading coffee shops...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Coffee className="text-amber-700" size={40} />
            <h1 className="text-4xl font-bold text-amber-900">Coffee Shop Reviews</h1>
          </div>
          <p className="text-amber-700">Discover and review the best coffee shops near you</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="text-red-600" size={24} />
            <div>
              <p className="text-red-800 font-medium">{error}</p>
              <button 
                onClick={fetchShops}
                className="text-red-600 underline text-sm mt-1"
              >
                Try again
              </button>
            </div>
          </div>
        )}

        {/* Search and Add Shop */}
        <div className="mb-6 flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search coffee shops..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <button
            onClick={() => setShowAddShop(true)}
            className="px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition flex items-center gap-2 font-medium"
          >
            <Plus size={20} />
            Add Shop
          </button>
        </div>

        {/* Database Notice */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          🗄️ Connected to SQL Server Database - All data is shared across users
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Shop List */}
          <div className="lg:col-span-1 space-y-4 max-h-[600px] overflow-y-auto">
            {filteredShops.length === 0 ? (
              <div className="p-8 bg-white rounded-lg shadow-md text-center text-gray-500">
                No coffee shops found. Add one to get started!
              </div>
            ) : (
              filteredShops.map(shop => (
                <div
                  key={shop.id}
                  onClick={() => {
                    setSelectedShop(shop);
                    setMapCenter({ lat: shop.lat, lng: shop.lng });
                  }}
                  className={`p-4 bg-white rounded-lg shadow-md cursor-pointer transition hover:shadow-lg ${
                    selectedShop?.id === shop.id ? 'ring-2 ring-amber-500' : ''
                  }`}
                >
                  <h3 className="font-bold text-lg text-amber-900 mb-1">{shop.name}</h3>
                  <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
                    <MapPin size={14} />
                    {shop.address}
                  </p>
                  {renderStars(shop.rating)}
                  <p className="text-sm text-gray-500 mt-2">{shop.reviews.length} reviews</p>
                </div>
              ))
            )}
          </div>

          {/* Map and Details */}
          <div className="lg:col-span-2">
            {/* Simple Map Visualization */}
            <div className="bg-white rounded-lg shadow-lg p-4 mb-4 h-80 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-green-100 to-blue-100">
                {/* Grid lines for map effect */}
                <div className="absolute inset-0 opacity-20">
                  {[...Array(20)].map((_, i) => (
                    <div key={`v${i}`} className="absolute h-full w-px bg-gray-400" style={{ left: `${i * 5}%` }} />
                  ))}
                  {[...Array(20)].map((_, i) => (
                    <div key={`h${i}`} className="absolute w-full h-px bg-gray-400" style={{ top: `${i * 5}%` }} />
                  ))}
                </div>
                
                {/* Shop markers */}
                {filteredShops.map(shop => {
                  const x = ((shop.lng + 123.1558) * 1000) % 100;
                  const y = ((shop.lat - 49.7016) * 1000) % 100;
                  return (
                    <div
                      key={shop.id}
                      onClick={() => {
                        setSelectedShop(shop);
                        setMapCenter({ lat: shop.lat, lng: shop.lng });
                      }}
                      className="absolute cursor-pointer transform -translate-x-1/2 -translate-y-1/2"
                      style={{ left: `${x}%`, top: `${y}%` }}
                    >
                      <div className={`relative ${selectedShop?.id === shop.id ? 'scale-125' : ''} transition`}>
                        <MapPin 
                          size={32} 
                          className={selectedShop?.id === shop.id ? "text-red-600 fill-red-400" : "text-amber-700 fill-amber-500"}
                        />
                        <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-xs font-medium bg-white px-2 py-1 rounded shadow">
                          {shop.name}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="absolute top-4 left-4 bg-white px-3 py-2 rounded shadow text-sm">
                <strong>Map Center:</strong> {mapCenter.lat.toFixed(4)}, {mapCenter.lng.toFixed(4)}
              </div>
            </div>

            {/* Selected Shop Details */}
            {selectedShop && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-amber-900 mb-1">{selectedShop.name}</h2>
                    <p className="text-gray-600 flex items-center gap-1">
                      <MapPin size={16} />
                      {selectedShop.address}
                    </p>
                    <div className="mt-2">{renderStars(selectedShop.rating)}</div>
                  </div>
                  <button
                    onClick={() => setShowAddReview(true)}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition text-sm font-medium"
                  >
                    Add Review
                  </button>
                </div>

                <div className="space-y-4 max-h-64 overflow-y-auto">
                  <h3 className="font-bold text-lg text-amber-900">Reviews</h3>
                  {selectedShop.reviews.length === 0 ? (
                    <p className="text-gray-500 italic">No reviews yet. Be the first to review!</p>
                  ) : (
                    selectedShop.reviews.map(review => (
                      <div key={review.id} className="border-b border-gray-200 pb-3">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold text-amber-900">{review.author}</p>
                            {renderStars(review.rating)}
                          </div>
                          <p className="text-sm text-gray-500">{review.date}</p>
                        </div>
                        <p className="text-gray-700">{review.comment}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Shop Modal */}
      {showAddShop && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-amber-900">Add Coffee Shop</h3>
              <button onClick={() => setShowAddShop(false)}>
                <X size={24} className="text-gray-500 hover:text-gray-700" />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Shop Name"
                value={newShop.name}
                onChange={(e) => setNewShop({ ...newShop, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <input
                type="text"
                placeholder="Address"
                value={newShop.address}
                onChange={(e) => setNewShop({ ...newShop, address: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <input
                type="number"
                step="0.0001"
                placeholder="Latitude (e.g., 49.7016)"
                value={newShop.lat}
                onChange={(e) => setNewShop({ ...newShop, lat: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <input
                type="number"
                step="0.0001"
                placeholder="Longitude (e.g., -123.1558)"
                value={newShop.lng}
                onChange={(e) => setNewShop({ ...newShop, lng: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <button
                onClick={addShop}
                className="w-full px-4 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition font-medium"
              >
                Add Shop
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Review Modal */}
      {showAddReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-amber-900">Add Review</h3>
              <button onClick={() => setShowAddReview(false)}>
                <X size={24} className="text-gray-500 hover:text-gray-700" />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                value={newReview.author}
                onChange={(e) => setNewReview({ ...newReview, author: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => setNewReview({ ...newReview, rating: star })}
                    >
                      <Star
                        size={32}
                        className={star <= newReview.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                placeholder="Your review..."
                value={newReview.comment}
                onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <button
                onClick={addReview}
                className="w-full px-4 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition font-medium"
              >
                Submit Review
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoffeeShopReviews;
